/// <amd-module name="Search/Package/Flux/StoresHub" />

import * as CompositeStoresManager from "Search/Shared/Base/Stores/CompositeStoresManager";
import { Action } from "VSS/Reflux/Action";
import { ActionsHub } from "Search/Package/Flux/ActionsHub";
import { IStoresHub } from "Search/Shared/Base/Container"
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore2";
import { IFilterStoreState } from "Search/Shared/Base/Stores/FilterStore";
import { SearchStore } from "Search/Package/Flux/Stores/SearchStore";
import { FilterStore } from "Search/Package/Flux/Stores/FilterStore";
import { ZeroDataStore } from "Search/Package/Flux/Stores/ZeroDataStore";
import { NotificationStore, INotificationStoreState } from "Search/Package/Flux/Stores/NotificationStore";
import { ZeroDataState } from "Search/Shared/Base/Stores/ZeroDataStore";
import { IVssPageContext } from "VSS/Platform/Context";
import { Filter } from "SearchUI/Utilities/Filter";
import { PackageSearchRequest, PackageSearchResponseContent } from "Search/WebApi/Contracts/Search.Shared";
import { IProjectData } from "Tfs/Platform/Page";

export type StoreName =
    "searchStore" |
    "filterStore" |
    "zeroDataStore" |
    "notificationStore";

export interface IAggregatedState {
    searchStoreState: ISearchStoreState<PackageSearchRequest, PackageSearchResponseContent>;
    filterStoreState: IFilterStoreState;
    zeroDataState: ZeroDataState;
    notificationStoreState: INotificationStoreState;
    isProjectContext: boolean;
}

export class StoresHub implements IStoresHub<StoreName, IAggregatedState> {
    public searchStore: SearchStore;
    public filterStore: FilterStore;
    public zeroDataStore: ZeroDataStore;
    public notificationStore: NotificationStore;

    private readonly compositeStoresManager = new CompositeStoresManager.CompositeStoresManager();
    private readonly listener: CompositeStoresManager.ListeningActionsManager;

    constructor(
        private readonly actionsHub: ActionsHub,
        private readonly pageContext: IVssPageContext,
        filter: Filter,
        private readonly project: IProjectData | undefined,
        private readonly onDispatched?: CompositeStoresManager.EmitChangedFunction<any>) {
        this.listener = new CompositeStoresManager.ListeningActionsManager(this.emitChanged);
        this.searchStore = this.createSearchStore(this.actionsHub);
        this.zeroDataStore = this.createZeroDataStore(this.actionsHub);
        this.filterStore = this.createFilterStore(this.actionsHub, filter);
        this.notificationStore = this.createNotificationStore(this.actionsHub);
    }

    public getAggregatedState = (): IAggregatedState => {
        return {
            searchStoreState: this.searchStore.state,
            filterStoreState: this.filterStore.state,
            zeroDataState: this.zeroDataStore.state,
            notificationStoreState: this.notificationStore.state,
            isProjectContext: !!this.project
        };
    }

    public getCompositeStore = (storeNames: StoreName[]): CompositeStoresManager.CompositeStore => {
        return this.compositeStoresManager.getOrCreate(storeNames);
    }

    public dispose = (): void => {
        this.listener.dispose();
        this.compositeStoresManager.dispose();
    };

    private createSearchStore(actionsHub: ActionsHub): SearchStore {
        const store = new SearchStore("SearchStoreChanged");

        this.listener.listen(actionsHub.pageInitializationStarted, "searchStore", (payload) => {
            payload.isLandingPage ? store.updateQuery(payload) : store.startSearch(payload);
        });
        this.listener.listen(actionsHub.resultsLoaded, "searchStore", store.loadSearchResults);
        this.listener.listen(actionsHub.searchStarted, "searchStore", store.startSearch);
        this.listener.listen(actionsHub.searchFailed, "searchStore", store.failSearch);

        return store;
    }

    private createFilterStore(actionsHub: ActionsHub, filter: Filter): FilterStore {
        const store = new FilterStore("FilterStoreChanged", filter, this.pageContext);

        this.listener.listen(actionsHub.resultsLoaded, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.updateFilters(searchStoreState.query, payload);
        });

        this.listener.listen(actionsHub.filterPaneToggled, "filterStore", store.toggleFilterPane);

        return store;
    }

    private createZeroDataStore(actionsHub: ActionsHub): ZeroDataStore {
        const store = new ZeroDataStore("ZeroDataStoreChanged");

        this.listener.listen(actionsHub.pageInitializationStarted, "zeroDataStore", (payload) => {
            if (payload.isLandingPage) {
                store.onLandingPage();
            }
        });
        this.listener.listen(actionsHub.resultsLoaded, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            const { query } = searchStoreState;
            const fetchMoreScenario = searchStoreState.fetchMoreScenario || false;
            store.onResultsLoaded(query, payload, fetchMoreScenario);
        });
        this.listener.listen(actionsHub.searchFailed, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.onSearchFailed(payload, searchStoreState.query);
        });
        this.listener.listen(actionsHub.searchStarted, "zeroDataStore", store.reset);

        return store;
    }

    private createNotificationStore(actionsHub: ActionsHub): NotificationStore {
        const store = new NotificationStore("NotificationStoreChanged");
        this.listener.listen(actionsHub.resultsLoaded, "notificationStore", store.onSearchResultsLoaded);
        return store;
    }
    private emitChanged = (changedStores: string[], action: Action<any>, payload: any): void => {
        this.compositeStoresManager.emitCompositeChanged(changedStores);

        if (this.onDispatched) {
            this.onDispatched(changedStores, action, payload);
        }
    };
}